import React from 'react';

/**
 * Monochrome wizard glyphs. Each draws with `currentColor` so the styled parent
 * controls the colour (per the area's "no raw colour literals" rule); multi-tone
 * marks (the Ask-Lumio sparkle) are tinted by their own component instead.
 */
type IconProps = { size?: number; className?: string };

export const HomeIcon = ({ size = 16, className }: IconProps): React.ReactElement => (
  <svg className={className} width={size} height={size} viewBox="0 0 24 24" fill="none" aria-hidden>
    <path
      d="M4 10.5 12 4l8 6.5V20a1 1 0 0 1-1 1h-4v-6h-6v6H5a1 1 0 0 1-1-1z"
      fill="currentColor"
    />
  </svg>
);

// Solar "Bold / Astronomy / Rocket 3" — filled bold glyph, pulled from Figma.
export const RocketIcon = ({ size = 24, className }: IconProps): React.ReactElement => (
  <svg className={className} width={size} height={size} viewBox="0 0 24 24" fill="none" aria-hidden>
    <path
      fillRule="evenodd"
      clipRule="evenodd"
      d="M2.77409 12.4814C3.07033 12.778 3.07004 13.2586 2.77343 13.5548L2.61779 13.7103C2.48483 13.8431 2.48483 14.058 2.61779 14.1908C2.75125 14.3241 2.96801 14.3241 3.10147 14.1908L4.8136 12.4807C5.1102 12.1845 5.59079 12.1848 5.88704 12.4814C6.18328 12.778 6.18298 13.2586 5.88638 13.5548L4.17426 15.2648C3.4481 15.9901 2.27116 15.9901 1.545 15.2648C0.818334 14.5391 0.818333 13.362 1.545 12.6362L1.70065 12.4807C1.99725 12.1845 2.47784 12.1848 2.77409 12.4814ZM7.68665 13.4896C7.98307 13.7861 7.98307 14.2667 7.68665 14.5631L5.54424 16.7055C5.24782 17.0019 4.76723 17.0019 4.4708 16.7055C4.17438 16.409 4.17438 15.9285 4.4708 15.632L6.61321 13.4896C6.90963 13.1932 7.39023 13.1932 7.68665 13.4896ZM10.4958 16.2953C10.7922 16.5918 10.7922 17.0724 10.4958 17.3688L8.36805 19.4965C8.07162 19.7929 7.59103 19.7929 7.29461 19.4965C6.99818 19.2001 6.99818 18.7195 7.29461 18.4231L9.42237 16.2953C9.71879 15.9989 10.1994 15.9989 10.4958 16.2953ZM7.29719 16.696C7.5903 16.9957 7.58495 17.4762 7.28525 17.7693L5.55508 19.4614C5.25538 19.7545 4.77481 19.7491 4.48171 19.4494C4.1886 19.1497 4.19395 18.6692 4.49365 18.3761L6.22382 16.684C6.52352 16.3909 7.00409 16.3963 7.29719 16.696ZM11.4811 18.118C11.7774 18.4146 11.7771 18.8952 11.4805 19.1915L9.76834 20.9015C9.63539 21.0343 9.63539 21.2492 9.76834 21.382C9.9018 21.5153 10.1186 21.5153 10.252 21.382L10.4077 21.2265C10.7043 20.9303 11.1849 20.9306 11.4811 21.2272C11.7774 21.5238 11.7771 22.0044 11.4805 22.3006L11.3248 22.4561C10.5987 23.1813 9.42171 23.1813 8.69556 22.4561C7.96889 21.7303 7.96889 20.5532 8.69556 19.8274L10.4077 18.1174C10.7043 17.8211 11.1849 17.8214 11.4811 18.118Z"
      fill="currentColor"
    />
    <path
      d="M10.8463 5.40912L8.65863 7.59023C8.2565 7.99113 7.88763 8.35888 7.59632 8.69132C7.40925 8.90481 7.2223 9.13847 7.06394 9.39666L7.04262 9.37541C7.00216 9.33507 6.98191 9.31487 6.96159 9.29516C6.58134 8.92621 6.13406 8.63287 5.64368 8.43084C5.61748 8.42004 5.59088 8.4095 5.53769 8.38842L5.21196 8.25936C4.77069 8.08451 4.65309 7.51645 4.98886 7.1817C5.9525 6.22099 7.10949 5.06751 7.66786 4.83584C8.1603 4.63152 8.69225 4.56354 9.20531 4.63936C9.67539 4.70883 10.1201 4.9503 10.8463 5.40912Z"
      fill="currentColor"
    />
    <path
      d="M14.5818 16.8932C14.7581 17.0722 14.8752 17.1985 14.981 17.3336C15.1207 17.5118 15.2456 17.7011 15.3544 17.8995C15.4769 18.1229 15.5721 18.3615 15.7623 18.8388C15.9172 19.2273 16.4317 19.33 16.7306 19.032L16.8029 18.9599C17.7665 17.9992 18.9235 16.8457 19.1558 16.289C19.3608 15.7981 19.429 15.2677 19.3529 14.7562C19.2832 14.2876 19.0411 13.8443 18.581 13.1204L16.386 15.3088C15.9748 15.7188 15.5977 16.0948 15.2567 16.3893C15.0523 16.5658 14.8287 16.7422 14.5818 16.8932Z"
      fill="currentColor"
    />
    <path
      fillRule="evenodd"
      clipRule="evenodd"
      d="M15.5023 14.3674L20.5319 9.35289C21.2563 8.63072 21.6185 8.26963 21.8092 7.81046C22 7.3513 22 6.84065 22 5.81937V5.33146C22 3.76099 22 2.97576 21.5106 2.48788C21.0213 2 20.2337 2 18.6585 2H18.1691C17.1447 2 16.6325 2 16.172 2.19019C15.7114 2.38039 15.3493 2.74147 14.6249 3.46364L9.59522 8.47817C8.74882 9.32202 8.224 9.84526 8.02078 10.3506C7.95657 10.5103 7.92446 10.6682 7.92446 10.8339C7.92446 11.5238 8.48138 12.0791 9.59522 13.1896L9.74492 13.3388L11.4985 11.5591C11.7486 11.3053 12.1571 11.3022 12.4109 11.5523C12.6647 11.8024 12.6678 12.2109 12.4177 12.4647L10.6587 14.2499L10.7766 14.3674C11.8905 15.4779 12.4474 16.0331 13.1394 16.0331C13.2924 16.0331 13.4387 16.006 13.5858 15.9518C14.1048 15.7607 14.6345 15.2325 15.5023 14.3674ZM17.8652 8.47854C17.2127 9.12904 16.1548 9.12904 15.5024 8.47854C14.8499 7.82803 14.8499 6.77335 15.5024 6.12284C16.1548 5.47233 17.2127 5.47233 17.8652 6.12284C18.5177 6.77335 18.5177 7.82803 17.8652 8.47854Z"
      fill="currentColor"
    />
  </svg>
);

export const ArrowRightIcon = ({ size = 20, className }: IconProps): React.ReactElement => (
  <svg className={className} width={size} height={size} viewBox="0 0 24 24" fill="none" aria-hidden>
    <path d="M5 12h13M13 6l6 6-6 6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

export const ArrowLeftIcon = ({ size = 24, className }: IconProps): React.ReactElement => (
  <svg className={className} width={size} height={size} viewBox="0 0 24 24" fill="none" aria-hidden>
    <path d="M19 12H6M11 6l-6 6 6 6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

export const ChevronDownIcon = ({ size = 20, className }: IconProps): React.ReactElement => (
  <svg className={className} width={size} height={size} viewBox="0 0 24 24" fill="none" aria-hidden>
    <path d="m6 9 6 6 6-6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

export const ChevronLeftIcon = ({ size = 20, className }: IconProps): React.ReactElement => (
  <svg className={className} width={size} height={size} viewBox="0 0 24 24" fill="none" aria-hidden>
    <path d="m15 6-6 6 6 6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

export const ChevronRightIcon = ({ size = 20, className }: IconProps): React.ReactElement => (
  <svg className={className} width={size} height={size} viewBox="0 0 24 24" fill="none" aria-hidden>
    <path d="m9 6 6 6-6 6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

export const SearchIcon = ({ size = 20, className }: IconProps): React.ReactElement => (
  <svg className={className} width={size} height={size} viewBox="0 0 24 24" fill="none" aria-hidden>
    <circle cx="11" cy="11" r="7" stroke="currentColor" strokeWidth="2" />
    <path d="m20 20-3.2-3.2" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
  </svg>
);

// Filled "earth / continents" globe — the go-live mark across the customizer
// (intro stage · progress chrome · deploy · connect-domain). Drawn with
// `currentColor` so every caller tints it via its parent and it adapts to
// light/dark automatically (the themed token sets the colour).
export const GlobeIcon = ({ size = 16, className }: IconProps): React.ReactElement => (
  <svg className={className} width={size} height={size} viewBox="0 0 15 15" fill="none" aria-hidden>
    <path
      d="M7.16667 0C5.74924 0 4.36364 0.420318 3.18508 1.2078C2.00653 1.99528 1.08796 3.11457 0.545533 4.4241C0.0031052 5.73364 -0.138819 7.17462 0.137709 8.56482C0.414236 9.95501 1.09679 11.232 2.09907 12.2343C3.10135 13.2365 4.37833 13.9191 5.76852 14.1956C7.15872 14.4722 8.5997 14.3302 9.90924 13.7878C11.2188 13.2454 12.3381 12.3268 13.1255 11.1483C13.913 9.9697 14.3333 8.5841 14.3333 7.16667C14.3312 5.2666 13.5755 3.44496 12.2319 2.10141C10.8884 0.757855 9.06674 0.00211746 7.16667 0ZM1 7.16667C1.00122 6.47624 1.11933 5.791 1.34934 5.14C1.76267 8.14667 4.4 8.40067 4.51334 8.88C4.57584 9.41986 4.59146 9.96412 4.56 10.5067C4.60667 11.4333 5.88667 11.4467 6.38 12.18C6.55339 12.5273 6.62086 12.918 6.574 13.3033C5.04908 13.1544 3.634 12.4438 2.60383 11.3097C1.57367 10.1755 1.00202 8.69884 1 7.16667ZM8.84 13.1C9.294 11.678 10.6573 11.196 10.796 10.374C10.9073 9.71467 10.0407 9.374 9.26267 9.28133C8.48467 9.18867 8.604 8.38467 8.05534 7.81467C7.50667 7.24467 7.14934 7.276 6.37867 7.37267C5.608 7.46933 4.94867 7.406 4.73334 6.932C4.518 6.458 4.79267 6.23133 4.226 5.644C4.1495 5.57069 4.09454 5.47783 4.06709 5.3755C4.03964 5.27316 4.04075 5.16526 4.07029 5.06351C4.09983 4.96176 4.15669 4.87004 4.23467 4.79832C4.31266 4.7266 4.40881 4.67761 4.51267 4.65667C4.91267 4.56 5.624 5.25667 6.04067 5.17467C7.29534 4.916 5.79134 2.45867 7.67667 1.84133C8.02277 1.72312 8.30814 1.47296 8.47067 1.14533C9.52817 1.37622 10.506 1.88215 11.3053 2.612C11.538 3.14643 11.6212 3.734 11.546 4.312C11.2947 5.09 10.6613 5.65 11.352 6.92267C12.0727 8.252 12.5173 8.43267 12.8413 8.06667C12.9681 7.92755 13.1266 7.82118 13.3033 7.75667C13.1829 8.99284 12.6926 10.164 11.8964 11.1172C11.1001 12.0704 10.035 12.7614 8.84 13.1Z"
      fill="currentColor"
    />
  </svg>
);

export const BoltIcon = ({ size = 20, className }: IconProps): React.ReactElement => (
  <svg className={className} width={size} height={size} viewBox="0 0 24 24" fill="none" aria-hidden>
    <path d="M13 2 4 14h6l-1 8 9-12h-6l1-8z" stroke="currentColor" strokeWidth="1.7" strokeLinejoin="round" />
  </svg>
);

export const LinkIcon = ({ size = 18, className }: IconProps): React.ReactElement => (
  <svg className={className} width={size} height={size} viewBox="0 0 24 24" fill="none" aria-hidden>
    <path
      d="M10 14a3.5 3.5 0 0 0 5 0l3-3a3.5 3.5 0 0 0-5-5l-1.5 1.5M14 10a3.5 3.5 0 0 0-5 0l-3 3a3.5 3.5 0 0 0 5 5L12.5 16.5"
      stroke="currentColor"
      strokeWidth="1.7"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

// "Open in new tab" box-with-arrow — same mark used by the website-settings Domain
// row (DomainsSection ExternalLinkIcon), drawn here with `currentColor` so the
// parent tints it. Trails the "Preview Website" CTA to signal it opens externally.
export const ExternalLinkIcon = ({ size = 18, className }: IconProps): React.ReactElement => (
  <svg className={className} width={size} height={size} viewBox="0 0 24 24" fill="none" aria-hidden>
    <path d="M14 5h5v5" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" />
    <path d="M19 5l-7 7" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" />
    <path
      d="M18 13.5v4A1.5 1.5 0 0 1 16.5 19h-10A1.5 1.5 0 0 1 5 17.5v-10A1.5 1.5 0 0 1 6.5 6h4"
      stroke="currentColor"
      strokeWidth="1.7"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

export const PencilIcon = ({ size = 20, className }: IconProps): React.ReactElement => (
  <svg className={className} width={size} height={size} viewBox="0 0 24 24" fill="none" aria-hidden>
    <path
      d="M4 20h4L18.5 9.5a2.1 2.1 0 0 0-3-3L5 17v3zM14.5 6.5l3 3"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

export const RefreshIcon = ({ size = 18, className }: IconProps): React.ReactElement => (
  <svg className={className} width={size} height={size} viewBox="0 0 24 24" fill="none" aria-hidden>
    <path
      d="M20 11a8 8 0 0 0-13.7-5.3L4 8M4 4v4h4M4 13a8 8 0 0 0 13.7 5.3L20 16m0 4v-4h-4"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

export const CopyIcon = ({ size = 16, className }: IconProps): React.ReactElement => (
  <svg className={className} width={size} height={size} viewBox="0 0 24 24" fill="none" aria-hidden>
    <rect x="9" y="9" width="11" height="11" rx="2" stroke="currentColor" strokeWidth="1.7" />
    <path d="M5 15H4a1 1 0 0 1-1-1V4a1 1 0 0 1 1-1h10a1 1 0 0 1 1 1v1" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" />
  </svg>
);

export const CheckIcon = ({ size = 16, className }: IconProps): React.ReactElement => (
  <svg className={className} width={size} height={size} viewBox="0 0 24 24" fill="none" aria-hidden>
    <path d="m5 12.5 4.5 4.5L19 7.5" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

export const InfoIcon = ({ size = 16, className }: IconProps): React.ReactElement => (
  <svg className={className} width={size} height={size} viewBox="0 0 24 24" fill="none" aria-hidden>
    <circle cx="12" cy="12" r="9" stroke="currentColor" strokeWidth="1.6" />
    <path d="M12 11v5" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
    <circle cx="12" cy="7.7" r="1.1" fill="currentColor" />
  </svg>
);

export const UploadCloudIcon = ({ size = 24, className }: IconProps): React.ReactElement => (
  <svg className={className} width={size} height={size} viewBox="0 0 24 24" fill="none" aria-hidden>
    <path
      d="M7 18a4 4 0 0 1-.5-7.97 5.5 5.5 0 0 1 10.7-1.2A4.001 4.001 0 0 1 17 18"
      stroke="currentColor"
      strokeWidth="1.6"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    <path d="M12 12v7M9 14.5 12 11.5l3 3" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

export const ImageIcon = ({ size = 20, className }: IconProps): React.ReactElement => (
  <svg className={className} width={size} height={size} viewBox="0 0 24 24" fill="none" aria-hidden>
    <rect x="3" y="4" width="18" height="16" rx="2.5" stroke="currentColor" strokeWidth="1.6" />
    <circle cx="8.5" cy="9.5" r="1.6" fill="currentColor" />
    <path d="m4 17 5-5 4 4 3-3 4 4" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

export const DiplomaIcon = ({ size = 20, className }: IconProps): React.ReactElement => (
  <svg className={className} width={size} height={size} viewBox="0 0 24 24" fill="none" aria-hidden>
    <circle cx="12" cy="9" r="6" stroke="currentColor" strokeWidth="1.6" />
    <path d="M9 14l-1 7 4-2.2L16 21l-1-7" stroke="currentColor" strokeWidth="1.6" strokeLinejoin="round" />
  </svg>
);

// "Sparkle light26" — the Ask-Lumio 4-point star, pulled from Figma. Drawn with
// currentColor so each caller tints it (the multi-tone Lumio gradient is applied
// by the parent, per the area's icon-tinting rule).
export const SparkleIcon = ({ size = 20, className }: IconProps): React.ReactElement => (
  <svg className={className} width={size} height={size} viewBox="0 0 20 20" fill="none" aria-hidden>
    <path
      d="M18.0886 10L12.4511 8.87883C11.7795 8.74528 11.2546 8.22032 11.121 7.54876L9.99985 1.91129L8.87867 7.54876C8.74512 8.22032 8.22016 8.74528 7.54859 8.87883L1.91113 10L7.54859 11.1212C8.22016 11.2547 8.74512 11.7797 8.87867 12.4513L9.99985 18.0887L11.121 12.4513C11.2546 11.7797 11.7795 11.2547 12.4511 11.1212L18.0886 10Z"
      fill="currentColor"
    />
  </svg>
);